/**
 * Single source of truth for "which permission unlocks which top-level area
 * of the app". Used by main-routing.module.ts (route guards),
 * sidebar.component.ts (menu visibility), and home.component.ts
 * (personalised quick-links/sections).
 *
 * Gating is PERMISSION-based, never role-based: a route/menu item declares
 * the permission it needs, and system.roles_permissions (assigned via the
 * backend's role/permission admin, not this file) determines which roles
 * actually get there. No role name should ever appear in routing, guard,
 * sidebar, or home-page code - if a role is missing access to something it
 * should have, the fix is assigning it the permission in the backend, not
 * adding its name to a list here.
 *
 * requiredPermission values are real backend permission names (verified
 * against system.permissions in the live dev DB) - the closest existing
 * "view" permission for each module. superadmin bypasses all permission
 * checks (AuthorizationService.hasPermission), so it doesn't need explicit
 * role_permissions rows for any of these.
 *
 * requiredPermission: null means "any authenticated user" - no specific
 * permission needed (Home, Settings).
 */

export interface AreaDefinition {
  path: string;
  label: string;
  icon: string;
  requiredPermission: string | null;
}

export type AreaKey =
  | 'home'
  | 'operations'
  | 'services'
  | 'portalManagement'
  | 'crm'
  | 'projects'
  | 'support'
  | 'communications'
  | 'systemManagement'
  | 'settings';

export const AREAS: Record<AreaKey, AreaDefinition> = {
  home: {
    path: '/home',
    label: 'Home',
    icon: 'pi pi-home',
    requiredPermission: null
  },
  operations: {
    path: '/operations',
    label: 'Operations',
    icon: 'pi pi-sitemap',
    requiredPermission: 'network_services.view'
  },
  services: {
    path: '/services',
    label: 'Services',
    icon: 'pi pi-th-large',
    requiredPermission: 'main_products.view'
  },
  portalManagement: {
    path: '/portal-management',
    label: 'Customer Portal',
    icon: 'pi pi-table',
    requiredPermission: 'accounts.view'
  },
  crm: {
    path: '/crm',
    label: 'CRM',
    icon: 'pi pi-briefcase',
    requiredPermission: 'leads.view'
  },
  projects: {
    path: '/projects',
    label: 'Projects',
    icon: 'pi pi-briefcase',
    requiredPermission: 'projects.view'
  },
  support: {
    path: '/support',
    label: 'Support',
    icon: 'pi pi-users',
    requiredPermission: 'support_tickets.view'
  },
  communications: {
    path: '/communications',
    label: 'Communications',
    icon: 'pi pi-envelope',
    requiredPermission: 'communication_templates.view'
  },
  systemManagement: {
    path: '/system-management',
    label: 'System Management',
    icon: 'pi pi-wrench',
    requiredPermission: 'users.view'
  },
  // Personal profile/preferences/password - every authenticated user needs
  // this regardless of what business areas they can reach.
  settings: {
    path: '/settings',
    label: 'Settings',
    icon: 'pi pi-cog',
    requiredPermission: null
  }
};
